import { api } from '@hunk/shared/api';
import type { SitePageLink } from '@hunk/shared/api/pages';
import type { ShopCategory, ShopDeliverySettings, ShopProduct } from '@hunk/shared/api/shop';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface ShopContextType {
  loading: boolean;
  error: string | undefined;
  categories: ShopCategory[];
  products: ShopProduct[];
  delivery: ShopDeliverySettings | undefined;
  /** content pages (top line + footer) */
  pages: SitePageLink[];
  byId: Map<number, ShopProduct>;
  reload: () => Promise<void>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

/** the whole public catalog (a couple of hundred products) is loaded once and filtered in the browser */
export function ShopProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [categories, setCategories] = useState<ShopCategory[]>([]);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [delivery, setDelivery] = useState<ShopDeliverySettings>();
  const [pages, setPages] = useState<SitePageLink[]>([]);

  const reload = useCallback(async () => {
    try {
      const r = (await api.shop.getCatalog()).response;
      setCategories(r.categories ?? []);
      setProducts(r.products ?? []);
      setDelivery(r.delivery);
      setPages(r.pages ?? []);
      setError(undefined);
    } catch {
      setError('לא ניתן לטעון את החנות כרגע, נסו לרענן');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => void reload(), [reload]);

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const value = useMemo(
    () => ({ loading, error, categories, products, delivery, pages, byId, reload }),
    [loading, error, categories, products, delivery, pages, byId, reload]
  );
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop(): ShopContextType {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error('useShop must be used inside ShopProvider');
  return ctx;
}
